/**
 * What the external path matches against the document, because it has no user to ask.
 *
 * Out of `EntryUpdateProvider.tsx:170-214` on #402. These three are deliberately *not* unified with
 * the in-app form: there, topics, project and people are chosen by the person typing. Here nothing
 * is chosen, so the text is matched against what the document already holds. #402 names this as a
 * divergence to keep rather than collapse.
 *
 * Pure functions over a resolved view, so they are testable against a real Automerge document with
 * no renderer — the same rule ADR 0012 rule 3 puts on `document/operations/`.
 */

import type { DocumentView } from '@/document'

/** Topics whose name appears in the text, restricted to those this entry type may carry. */
export function matchTopics(
  doc: DocumentView,
  searchText: string,
  entryType: string,
): string[] {
  if (!doc.topics) return []
  return Object.values(doc.topics)
    .filter((t) => !t.archivedAt && (!t.entryType || t.entryType === entryType))
    .filter((t) => searchText.includes(t.name.toLowerCase()))
    .map((t) => t.id)
}

/**
 * The first project whose name appears in the text, or none.
 *
 * A name under three characters has to appear as a whole word: without that, a project called "AI"
 * matches "wait".
 */
export function matchProject(
  doc: DocumentView,
  searchText: string,
): string | null {
  if (!doc.projects) return null
  const matched = Object.values(doc.projects)
    .filter((p) => !p.archivedAt)
    .find((p) => {
      const name = p.name.toLowerCase()
      if (name.length < 3) {
        const regex = new RegExp(
          `\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
        )
        return regex.test(searchText)
      }
      return searchText.includes(name)
    })
  return matched ? matched.id : null
}

/** People in the document whose full name or first name appears in the text. */
export function matchPeople(doc: DocumentView, searchText: string): string[] {
  if (!doc.entries) return []
  const ids: string[] = []
  for (const person of Object.values(doc.entries)) {
    if (person.type !== 'person' || person.archivedAt) continue
    const name = person.title.toLowerCase()
    const firstName = name.split(/[\s-]/)[0]
    if (searchText.includes(name) || searchText.includes(firstName)) {
      ids.push(person.id)
    }
  }
  return ids
}
